/**
 * The thickness slider — SRS C3.
 *
 * The slider holds a target SCREEN width: how thick the line feels under the hand,
 * whatever the zoom. The number beside it is the canvas-space width, which is what
 * actually gets stored, and it changes as the teacher zooms. That difference is the
 * whole of C3 and it is deliberate.
 *
 * The range is 1–20 screen px with the default 3 sitting exactly at the centre of the
 * travel, so the two halves of the slider are scaled differently.
 */

import {
  clamp,
  DEFAULT_TARGET_WIDTH_PX,
  MAX_TARGET_WIDTH_PX,
  MIN_TARGET_WIDTH_PX,
} from '@opal/shared';

/** The slider runs 0–100 so the thumb has somewhere precise to sit at 50. */
export const SLIDER_MIN = 0;
export const SLIDER_MAX = 100;
export const SLIDER_CENTRE = 50;

function round(px: number): number {
  return Math.round(px * 10) / 10;
}

/** Slider position (0–100) to the target screen width in pixels. */
export function sliderToScreenWidth(position: number): number {
  const at = clamp(position, SLIDER_MIN, SLIDER_MAX);
  if (at <= SLIDER_CENTRE) {
    const share = at / SLIDER_CENTRE;
    return round(MIN_TARGET_WIDTH_PX + share * (DEFAULT_TARGET_WIDTH_PX - MIN_TARGET_WIDTH_PX));
  }
  const share = (at - SLIDER_CENTRE) / (SLIDER_MAX - SLIDER_CENTRE);
  return round(DEFAULT_TARGET_WIDTH_PX + share * (MAX_TARGET_WIDTH_PX - DEFAULT_TARGET_WIDTH_PX));
}

/** Target screen width back to a slider position, so the thumb sits where it belongs. */
export function screenWidthToSlider(px: number): number {
  const width = clamp(px, MIN_TARGET_WIDTH_PX, MAX_TARGET_WIDTH_PX);
  if (width <= DEFAULT_TARGET_WIDTH_PX) {
    const share =
      (width - MIN_TARGET_WIDTH_PX) / (DEFAULT_TARGET_WIDTH_PX - MIN_TARGET_WIDTH_PX);
    return Math.round(share * SLIDER_CENTRE);
  }
  const share =
    (width - DEFAULT_TARGET_WIDTH_PX) / (MAX_TARGET_WIDTH_PX - DEFAULT_TARGET_WIDTH_PX);
  return Math.round(SLIDER_CENTRE + share * (SLIDER_MAX - SLIDER_CENTRE));
}
